import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const INPUT = "w-full bg-[#050505] border border-white/10 rounded px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:border-white/30 transition-colors";

export default function MongoServerEditor({ row, environments, linkedBy, onSaved, onCancel }) {
    const [envId, setEnvId] = useState(row.env_id || "");
    const [url, setUrl] = useState(row.url || "");
    const [token, setToken] = useState("");
    const [clearToken, setClearToken] = useState(false);
    const [busy, setBusy] = useState(false);
    const id = row.id;
    const save = async () => {
        const body = {};
        if (envId !== (row.env_id || "")) body.env_id = envId;
        if (url.trim() !== (row.url || "")) body.url = url.trim();
        if (token.trim()) body.token = token.trim();
        if (clearToken) body.clear_token = true;
        if (!Object.keys(body).length) { onCancel(); return; }
        setBusy(true);
        try { await api.put(`/admin/mongo-mcp/servers/${id}`, body); toast.success(`${row.label} saved`); onSaved(); }
        catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div className="mt-3 border-t border-white/10 pt-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid={`mongo-server-editor-${id}`}>
            <label className="text-[10px] uppercase tracking-wider text-white/40 space-y-1">
                <span>Radar environment</span>
                <select value={envId} onChange={(e) => setEnvId(e.target.value)} className={INPUT} data-testid={`mongo-server-env-select-${id}`}>
                    <option value="">Not linked</option>
                    {environments.map((e) => {
                        const taken = linkedBy[e.env_id] && linkedBy[e.env_id] !== row.label;
                        const tags = [e.region, e.channel].filter(Boolean).join(" · ");
                        return <option key={e.env_id} value={e.env_id} disabled={taken}>{e.name}{tags ? ` (${tags})` : ""}{taken ? ` — linked to ${linkedBy[e.env_id]}` : ""}</option>;
                    })}
                </select>
            </label>
            <label className="text-[10px] uppercase tracking-wider text-white/40 space-y-1">
                <span>MCP server address</span>
                <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://mongodb-mcp.internal:3000/mcp" className={INPUT}
                    data-testid={`mongo-server-url-input-${id}`} />
            </label>
            <label className="text-[10px] uppercase tracking-wider text-white/40 space-y-1">
                <span>Access token {row.token_set && <span className="normal-case tracking-normal text-white/30">— stored {row.token_mask}, leave blank to keep</span>}</span>
                <input type="password" autoComplete="new-password" value={token} onChange={(e) => setToken(e.target.value)} disabled={clearToken}
                    placeholder={row.token_set ? "••••••••" : "paste the access token"} className={`${INPUT} disabled:opacity-40`} data-testid={`mongo-server-token-input-${id}`} />
            </label>
            <div className="md:col-span-3 flex flex-wrap items-center justify-between gap-2">
                {row.token_set ? (
                    <label className="flex items-center gap-1.5 text-[11px] text-white/55 cursor-pointer">
                        <input type="checkbox" checked={clearToken} onChange={(e) => setClearToken(e.target.checked)} data-testid={`mongo-server-clear-token-${id}`} />
                        Remove the stored token
                    </label>
                ) : <span className="text-[11px] text-white/35">The token is stored encrypted and never shown again.</span>}
                <div className="flex gap-2">
                    <button onClick={onCancel} className="px-3 py-1.5 text-xs rounded-md border border-white/10 hover:bg-white/5 transition-colors"
                        data-testid={`mongo-server-cancel-${id}`}>Cancel</button>
                    <button onClick={save} disabled={busy} className="px-3 py-1.5 text-xs rounded-md bg-white text-black hover:bg-white/90 disabled:opacity-50 transition-colors"
                        data-testid={`mongo-server-save-${id}`}>{busy ? "Saving…" : "Save"}</button>
                </div>
            </div>
        </div>
    );
}
